/* eslint-disable no-param-reassign */
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Modal } from 'react-bootstrap';
import classNames from 'classnames';
import { showNotification } from '../../../redux/notification';
import uploadFileIcon from '../../../assets/images/upload-file.svg';
import attachFileIcon from '../../../assets/images/attach-file.svg';
import LoaderIcon from '../../../assets/images/loader.svg';
import BuildPLUploadForm from './BuildPLUploadForm';

import './UploadCredentialModal.scss';

const UploadPLCredentialModal = ({
	caregiverId,
	managementCompanyId,
	onClose,
	onSuccess = () => {},
	allowedPLsubtypes,
	credentialByTypeQuery,
	uploadCredentialMutation,
	primaryLicense,
	credentialData,
	DownloadButton
}) => {
	const dispatch = useDispatch();

	const [formFields, setFormFields] = useState([]);
	const [data, setData] = useState({});
	const [isLicenseNumberRequired, setIsLicenseNumberRequired] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [validationErrors, setValidationErrors] = useState([]);
	const [fileError, setFileError] = useState('');
	const [hasChanges, setHasChanges] = useState(false);

	const ALLOWED_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
	const credType = 'PROFESSIONAL_LICENSE';

	const formFieldKeys = formFields?.map((field) => field.name) ?? [];

	const isEdit = !!credentialData;

	const { data: credentialByType } = credentialByTypeQuery(
		{ caregiverId, managementCompanyId, credType, source: 'pl-modal' },
		{
			refetchOnMountOrArgChange: true
		}
	);

	const [uploadCredential, { isLoading: isUploading }] = uploadCredentialMutation();

	useEffect(() => {
		setValidationErrors([]);
		setFormFields([]);
		if (credentialData) {
			setData({ ...credentialData, status: credentialData.status_code, certification_type: credentialData.short_name });
		}
	}, []);

	useEffect(() => {
		if (allowedPLsubtypes?.size > 0) {
			const result = credentialByType?.editUIConfig.map((field) => {
				if (field.name === 'certification_type') {
					return {
						...field,
						values: field.values.filter((certificationType) => allowedPLsubtypes.has(certificationType.name))
					};
				}
				return field;
			});

			setFormFields(result);
		} else {
			setFormFields(credentialByType?.editUIConfig);
		}
	}, [credentialByType, allowedPLsubtypes]);

	useEffect(() => {
		if (!isLicenseNumberRequired) {
			setValidationErrors((oldValidationErrors) => [
				...oldValidationErrors.filter((err) => err.field !== 'licence_number')
			]);
		}
	}, [isLicenseNumberRequired]);

	const updateData = (name, value) => {
		setData((oldState) => ({ ...oldState, [name]: value }));
		setHasChanges(true);
	};

	const getValidationErrorMessage = useCallback(
		(fieldName) => {
			const error = validationErrors.find((item) => item.field === fieldName);
			return error && error.message;
		},
		[validationErrors]
	);

	useEffect(() => {
		setFileError(getValidationErrorMessage('attachment_id'));
	}, [validationErrors, getValidationErrorMessage]);

	const handleUploadFile = async (event) => {
		event.stopPropagation();
		event.preventDefault();
		const file = event.target.files[0];
		const fileSizeInMb = Math.round(file.size / (1024 * 1024));
		if (file && fileSizeInMb > 50) {
			event.target.value = null;
			dispatch(showNotification('error', 'File must not exceed 50MB'));
			return;
		}
		if (file && ALLOWED_MIME_TYPES.indexOf(file.type.toLowerCase()) === -1) {
			event.target.value = null;
			dispatch(showNotification('error', 'Error uploading files. Only PDF, JPG and PNG files are allowed'));
			return;
		}
		if (!file) {
			event.target.value = null;
			return;
		}
		updateData('attachment', file);
		setFileError('');
	};

	const handleSubmitForm = async () => {
		const allErrors = [];
		try {
			setSubmitting(true);
			if (isLicenseNumberRequired && !data.licence_number) {
				allErrors.push({
					field: 'licence_number',
					message: '"License number" is required.'
				});
			}
			// replace short_code (CNA) with code (CERT_NURSE_ASSIST) for certification_type
			const certType = data.certification_type; // short_code
			const certTypeFieldValues = formFields.find((item) => item.name === 'certification_type')?.values;
			const certTypeValue = certTypeFieldValues.find((item) => item.name === certType);
			const certTypeCode = certTypeValue?.code;

			if (!certTypeCode) {
				allErrors.push({
					field: 'certification_type',
					message: '"License type" is required.'
				});
			}

			const formData = new FormData();

			formFieldKeys.forEach((key) => {
				const value = data[key];
				if (key === 'attachment') {
					if (value) {
						formData.append(key, value);
					}
					return;
				}
				if (key === 'certification_type') {
					formData.append(key, certTypeCode);
					return;
				}
				formData.append(key, value?.trim() || '');
			});

			if (allErrors.length) {
				// eslint-disable-next-line no-throw-literal
				throw {
					data: {
						errors: allErrors,
						validationError: true
					}
				};
			}

			await uploadCredential({
				caregiverId,
				managementCompanyId,
				credType,
				formData,
				credentialId: credentialData ? credentialData.id : undefined
			}).unwrap();
			onSuccess();
		} catch (error) {
			const defaultError = 'There was a problem uploading your credential.';
			const { errors, validationError } = error.data;
			if (validationError && errors.length) {
				allErrors.push(...errors);
				setValidationErrors(allErrors);
			}
			if (!validationError) {
				dispatch(showNotification('error', defaultError));
			}
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Modal
			show
			centered
			className={classNames('upload-credential-modal', { 'is-loading': isUploading || submitting })}
			backdrop="static"
			onHide={onClose}
			keyboard={false}
		>
			<Modal.Header closeButton={!primaryLicense}>
				<Modal.Title>
					{isEdit ? 'Edit professional license' : <>Upload {primaryLicense ? 'primary' : 'professional'} license</>}
				</Modal.Title>
			</Modal.Header>

			<Modal.Body>
				<div className="upper-container">
					{(submitting || isUploading) && <div className="gray-overlay" />}
					<div className="left-side">
						<div className="first-row">
							<label className="select-file-btn btn-light-purple" htmlFor="uploadFile">
								<img src={uploadFileIcon} alt="" />
								<span>Select file from computer</span>
								<input type="file" id="uploadFile" style={{ display: 'none' }} onChange={handleUploadFile} />
							</label>

							<span className="upload-info">
								File must not exceed 50 MB. To replace a currently uploaded file, upload a new one.
							</span>
						</div>

						{fileError && <div className="file-error">{fileError}</div>}

						{data.attachment && (
							<div className="file-attachment">
								<img src={attachFileIcon} alt="" />
								<div className="attachment-name">
									{isEdit ? data.attachment?.name || 'File attached' : data.attachment?.name}
								</div>
								{!data.attachment?.name && DownloadButton}
							</div>
						)}
					</div>
					<div className="right-side">
						<BuildPLUploadForm
							formFields={formFields || []}
							data={data}
							updateData={updateData}
							isLicenseNumberRequired={isLicenseNumberRequired}
							setIsLicenseNumberRequired={setIsLicenseNumberRequired}
							getValidationErrorMessage={getValidationErrorMessage}
							setValidationErrors={setValidationErrors}
						/>
					</div>
				</div>
			</Modal.Body>

			<Modal.Footer>
				<div className="buttons">
					<button type="button" className="btn-purple__text" onClick={onClose} disabled={isUploading || submitting}>
						{primaryLicense ? 'Upload credentials later' : 'Cancel'}
					</button>
					<button
						type="button"
						className="btn-purple"
						onClick={handleSubmitForm}
						disabled={isUploading || submitting || (!hasChanges && isEdit)}
					>
						{isUploading ? (
							<>
								<img aria-hidden src={LoaderIcon} alt="loader" className="button-loading-spinner" /> Uploading...
							</>
						) : (
							<>
								{isEdit && <>Save changes</>}
								{!isEdit && <>Upload</>}
							</>
						)}
					</button>
				</div>
			</Modal.Footer>
		</Modal>
	);
};

export default UploadPLCredentialModal;
